import html2pdf from 'html2pdf.js';
import CoverPage from './CoverPage';
import DeclarationPage from './DeclarationPage';
import PG3Bg from "/assets/PdfNew/PG3Bg.png";
import PG4Bg from "/assets/PdfNew/PG4.png";
import PG15Bg from "/assets/PdfNew/pgbg15.png";
import PG17Bg from "/assets/PdfNew/pgbg17.png";
import FactorDescription from './FactorDescription';
import PersonalityAnalysis from './PersonalityAnalysis';
import CareerOption from './CareerOption';
import WordFromYou from './WordFromYou';
import WayForward from './WayForward';
import PersonalityResult from './PersonalityResult';
import PredicatedCareerOption from './PredicatedCareerOption';
import { Box } from '@mui/material';
import PredicatedCareerTable from './PredicatedCareerOption/PredicatedCareerTable';
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

const GeneratePdf = ({ careerPdfData = null, onPdfGenerated, phase, setPhase }) => {
  const hasUploaded = useRef(false);
  const generationStarted = useRef(false);
  const generatedPdfBlob = useRef(null);

  const studentName = careerPdfData?.pdf_report?.student_name || "";
  const filename = useMemo(
    () => `${studentName}_Swayamrise_Career_Assessment_Report_${new Date().toISOString().split("T")[0]}.pdf`,
    [studentName],
  );

  const options = useMemo(
    () => ({
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.9 },
      html2canvas: {
        scale: 1,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 794,
      },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      enableLinks: false,
    }),
    [filename],
  );

  useEffect(() => {
    if (generationStarted.current || !careerPdfData) return;

    const element = document.getElementById('pdf-content');
    if (!element) return;
    generationStarted.current = true;

    const allImages = element.querySelectorAll('img');

    const generateAndUpload = async () => {
      setPhase?.('generating');
      try {
        const pdf = await html2pdf()
          .set(options)
          .from(element)
          .toPdf()
          .get('pdf');

        generatedPdfBlob.current = pdf.output('blob');
        if (onPdfGenerated && !hasUploaded.current) {
          hasUploaded.current = true;
          setPhase?.('uploading');
          await onPdfGenerated(generatedPdfBlob.current, filename);
        }
      } catch (error) {
        console.error('Error generating PDF:', error);
        setPhase?.('error');
      }
    };

    const imagePromises = Array.from(allImages).map((img) => {
      if (img.complete && img.naturalWidth > 0) {
        return typeof img.decode === 'function'
          ? img.decode().catch(() => undefined)
          : Promise.resolve();
      }
      return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          img.removeEventListener('load', finish);
          img.removeEventListener('error', finish);
          resolve();
        };
        const timeoutId = setTimeout(finish, 10000);
        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });
      });
    });

    const fontsReady = document.fonts?.ready || Promise.resolve();
    Promise.all([fontsReady, ...imagePromises])
      .then(
        () => new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        }),
      )
      .then(generateAndUpload)
      .catch((error) => {
        console.error('Error preparing PDF:', error);
        setPhase?.('error');
      });
  }, [careerPdfData, filename, onPdfGenerated, options, setPhase]);

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!generatedPdfBlob.current) return;

    setPhase?.('downloading');
    const objectUrl = URL.createObjectURL(generatedPdfBlob.current);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    setPhase?.('done');
  };

  const isDownloading = phase === 'downloading';
  const canDownload = phase === 'done' || phase === 'downloading';

  return (
    <>
      <div
        style={{
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: "794px",
          opacity: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <div id="pdf-content">
          <CoverPage studentData={careerPdfData?.pdf_report} school_board_name={careerPdfData?.school_board_name}></CoverPage>
          <div className="html2pdf__page-break"></div>
          <DeclarationPage  studentData={careerPdfData?.pdf_report}></DeclarationPage>
          <div className="html2pdf__page-break"></div>
          <div className="PDFIMGContainer">
            <img src={PG3Bg} alt="background" className="PDFIMGContainer" />
          </div>
          <div className="html2pdf__page-break"></div>
          <div className="PDFIMGContainer">
            <img src={PG4Bg} alt="background" className="PDFIMGContainer" />
          </div>
          <div className="html2pdf__page-break"></div>
          <FactorDescription></FactorDescription>
          <div className="html2pdf__page-break"></div>
          <PersonalityAnalysis></PersonalityAnalysis>
          <div className="html2pdf__page-break"></div>
          <PersonalityResult big_results={careerPdfData?.pdf_report?.big_results || []} personality_data={careerPdfData?.pdf_report?.personality_test_result || []} ></PersonalityResult>
          <div className="html2pdf__page-break"></div>
          <CareerOption></CareerOption>
          <div className="html2pdf__page-break"></div>
          {
            careerPdfData?.career_info &&
            careerPdfData.career_info.map((careerOption, index) => (
              <React.Fragment key={index}>
                <PredicatedCareerOption careerOption={careerOption} />
                <div className="html2pdf__page-break" />
                <PredicatedCareerTable careerOption={careerOption} notes={index === careerPdfData.career_info.length - 1} />
                <div className="html2pdf__page-break" />
              </React.Fragment>
            ))
          }
                    <div><WayForward></WayForward></div>

          <div className="PDFIMGContainer">
            <img src={PG15Bg} alt="background" className="PDFIMGContainer" />
          </div>
          <div className="html2pdf__page-break"></div>
          <WordFromYou></WordFromYou>
          <div className="html2pdf__page-break"></div>
          <img style={{ maxWidth: '100%', height: '1112px' }} src={PG17Bg} alt='PG-Bg'></img>
        </div>
      </div>

      {canDownload && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            onClick={!isDownloading ? handleDownload : undefined}
            sx={{
              cursor: isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <img src="/assets/newImages/downloadButton.png" alt="Download Report" />
          </Box>
        </Box>
      )}
    </>
  );
};

GeneratePdf.propTypes = {
  careerPdfData: PropTypes.object,
  onPdfGenerated: PropTypes.func,
  phase: PropTypes.string,
  setPhase: PropTypes.func,
};

export default GeneratePdf;
